import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faTimes,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const SprintCentralVelocity = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { projectId } = useParams();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [velocityData, setVelocityData] = useState([]);
  const [search, setSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  const [showModal, setShowModal] = useState(false);
  const [editingVelocity, setEditingVelocity] = useState(null);

  const initialForm = {
    sprint: "",
    plannedPoints: "",
    completedPoints: "",
    team: "",
    date: "",
  };

  const [formData, setFormData] = useState(initialForm);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  // -----------------------
  // Sample Data
  // -----------------------

  const sampleVelocity = [
    {
      id: 1,
      projectId: 1,
      sprint: "Sprint 1",
      plannedPoints: 35,
      completedPoints: 28,
      team: "Frontend Team",
      date: "2026-03-01",
    },
    {
      id: 2,
      projectId: 1,
      sprint: "Sprint 2",
      plannedPoints: 40,
      completedPoints: 32,
      team: "Frontend Team",
      date: "2026-03-15",
    },
    {
      id: 3,
      projectId: 1,
      sprint: "Sprint 3",
      plannedPoints: 38,
      completedPoints: 26,
      team: "Frontend Team",
      date: "2026-03-30",
    },
  ];

  const loadSample = () => {
    setVelocityData(sampleVelocity);
  };

  // -----------------------
  // CRUD
  // -----------------------

  const handleAddNew = () => {
    setEditingVelocity(null);
    setFormData(initialForm);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingVelocity(item);

    setFormData({
      sprint: item.sprint,
      plannedPoints: item.plannedPoints,
      completedPoints: item.completedPoints,
      team: item.team,
      date: item.date,
    });

    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete velocity record?")) return;

    setVelocityData((prev) => prev.filter((v) => v.id !== id));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const newVelocity = {
      id: editingVelocity?.id || Date.now(),
      projectId: Number(projectId || 1),
      sprint: formData.sprint,
      plannedPoints: Number(formData.plannedPoints),
      completedPoints: Number(formData.completedPoints),
      team: formData.team,
      date: formData.date,
    };

    if (editingVelocity) {
      setVelocityData((prev) =>
        prev.map((v) => (v.id === editingVelocity.id ? newVelocity : v)),
      );
    } else {
      setVelocityData((prev) => [...prev, newVelocity]);
    }

    setShowModal(false);
    setFormData(initialForm);
  };

  // -----------------------
  // SEARCH
  // -----------------------

  const filtered = velocityData.filter(
    (v) =>
      v.sprint.toLowerCase().includes(search.toLowerCase()) ||
      v.team.toLowerCase().includes(search.toLowerCase()),
  );

  const totalPages = Math.ceil(filtered.length / perPage);

  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage,
  );

  const velocityStatus = (planned, completed) => {
    if (completed >= planned) return "bg-emerald-600";
    if (completed >= planned * 0.7) return "bg-yellow-600";

    return "bg-red-600";
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-2">
      {/* HEADER */}

      <header className="sticky top-0 z-50 bg-black border-b border-blue-500/40 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-cyan-300">
            🚀 SprintCentral Velocity
          </div>

          <div className="text-xs text-gray-400">
            Sprint Performance Analytics
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn ? (
            <>
              <span className="text-xs text-teal-300">{userName}</span>

              <button
                onClick={handleLogout}
                className="w-12 px-2 py-1 bg-red-700/80 hover:bg-red-600 rounded-md text-sm"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-12 px-2 py-1 border border-teal-600 rounded-md"
            >
              🔐
            </button>
          )}
        </div>
      </header>
      
      <div className="mt-2 mb-2 min-h-[70vh]">
        {/* TOOLBAR */}

        <div className="mt-4 flex flex-col lg:flex-row gap-4">
            <button
            onClick={loadSample}
            className="text-xs lg:text-sm w-48 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
            >
            <FontAwesomeIcon icon={faPlus} /> Sample Velocity
            </button>

            <button
            onClick={handleAddNew}
            className="text-xs lg:text-sm w-48 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
            >
            <FontAwesomeIcon icon={faPlus} /> Add Velocity
            </button>

            <div className="relative w-full">
            <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />

            <input
                type="text"
                placeholder="Search sprint velocity..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 pr-4 py-2 w-full bg-gray-800 border border-gray-700 rounded-lg"
            />
            </div>
        </div>

        {/* MODAL */}

        {showModal && (
            <div className="bg-black w-full rounded-lg p-4 border border-cyan-200 shadow-xl mt-4">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg text-cyan-200 font-semibold">
                {editingVelocity ? "Edit Velocity" : "Add Velocity"}
                </h2>

                <button
                className="w-12 bg-blue-500"
                onClick={() => setShowModal(false)}
                >
                <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
                <input
                type="text"
                placeholder="Sprint Name"
                value={formData.sprint}
                required
                onChange={(e) =>
                    setFormData({ ...formData, sprint: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2"
                />

                <div className="grid grid-cols-2 gap-3">
                <input
                    type="number"
                    placeholder="Planned Story Points"
                    value={formData.plannedPoints}
                    onChange={(e) =>
                    setFormData({ ...formData, plannedPoints: e.target.value })
                    }
                    className="w-full bg-gray-900 border border-gray-700 rounded p-2"
                />

                <input
                    type="number"
                    placeholder="Completed Story Points"
                    value={formData.completedPoints}
                    onChange={(e) =>
                    setFormData({ ...formData, completedPoints: e.target.value })
                    }
                    className="w-full bg-gray-900 border border-gray-700 rounded p-2"
                />
                </div>

                <input
                type="text"
                placeholder="Team"
                value={formData.team}
                onChange={(e) =>
                    setFormData({ ...formData, team: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2"
                />

                <input
                type="date"
                value={formData.date}
                onChange={(e) =>
                    setFormData({ ...formData, date: e.target.value })
                }
                className="w-48 bg-white border border-gray-700 rounded p-2 text-black"
                />

                <button
                type="submit"
                className="ml-4 w-40 bg-cyan-600 hover:bg-cyan-500 rounded p-2"
                >
                {editingVelocity ? "Update" : "Create"}
                </button>
            </form>
            </div>
        )}

        {/* GRID */}

        <div className="mt-6 grid gap-4 grid-cols-1 lg:grid-cols-4">
            {paginated.map((v) => {
            const velocity = v.completedPoints;

            return (
                <div
                key={v.id}
                className="bg-gray-800 border border-gray-700 rounded-xl p-5"
                >
                <div className="flex justify-between">
                    <h3 className="font-semibold text-white">{v.sprint}</h3>

                    <span
                    className={`px-2 py-1 rounded text-xs ${velocityStatus(v.plannedPoints, v.completedPoints)}`}
                    >
                    Velocity
                    </span>
                </div>

                <div className="text-sm text-gray-300 mt-3">
                    Planned: {v.plannedPoints} <br />
                    Completed: {v.completedPoints}
                </div>

                <div className="text-2xl text-yellow-400 mt-2">{velocity}</div>

                <div className="text-xs text-gray-400">
                    Story Points Delivered
                </div>

                <div className="text-xs text-gray-500 mt-2">Team: {v.team}</div>

                <div className="mt-4 flex gap-2">
                    <button
                    onClick={() => handleEdit(v)}
                    className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                    >
                    <FontAwesomeIcon icon={faEdit} />
                    </button>

                    <button
                    onClick={() => handleDelete(v.id)}
                    className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                    >
                    <FontAwesomeIcon icon={faTrash} />
                    </button>
                </div>
                </div>
            );
            })}
        </div>

        {/* PAGINATION */}

        {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-10">
            <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="p-2 bg-gray-800 border border-gray-700 rounded-lg"
            >
                <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            <span className="text-sm">
                Page {currentPage} of {totalPages}
            </span>

            <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="p-2 bg-gray-800 border border-gray-700 rounded-lg"
            >
                <FontAwesomeIcon icon={faChevronRight} />
            </button>
            </div>
        )}
      </div>

      {/* FOOTER */}

      <footer className="bg-black border-t border-blue-400 text-white py-4 mt-10">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">
            © 2026 Think5Whys Global DeepTech Solutions.
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">
              About
            </Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">
              Privacy Policy
            </Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">
              Terms
            </Link>
            <Link to="/contact-us" className="hover:text-teal-300">
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SprintCentralVelocity;
